/**
 * VAT of a jurisdiction with the GENERIC_VAT scheme (#181): the rates the
 * company defines (`TenantVatRate`), applied per line.
 *
 * Arithmetic is exact: prices and discounts in hundredths, quantities in
 * thousandths, rates in ten-thousandths, as bigint. Each stored amount is
 * rounded once, to the centime, an exact half away from zero:
 *
 *   HT  mode: netHT = round(price × quantity − discount); vat = round(netHT × rate); ttc = netHT + vat
 *   TTC mode: ttc = round(price × quantity − discount); netHT = round(ttc / (1 + rate)); vat = ttc − netHT
 *
 * Totals per rate and per invoice are sums of the line values. No TSB,
 * specific tax, PSVB or stamp duty exists in this scheme. The net to pay is
 * the total TTC in the payable unit of the currency (`toPayableAmount`).
 *
 * This module never reaches the Burkina Faso engine or the MCF emulation
 * (tax-calculator.ts, constants/tax-groups.ts, @efacture/mcf-protocol); a
 * test walks its imports.
 */
import { InvoiceError } from "../errors/index.js";
import { toPayableAmount } from "./payable-amount.js";
/** `value` × 10^digits as a bigint, or null when it has more decimals. */
function scaled(value, digits) {
    if (!Number.isFinite(value))
        return null;
    const factor = 10 ** digits;
    const product = Number((value * factor).toFixed(6));
    return Number.isInteger(product) ? BigInt(product) : null;
}
/** `numerator / denominator` to the nearest integer, an exact half away from zero. */
function divideHalfAway(numerator, denominator) {
    const negative = numerator < 0n !== denominator < 0n;
    const n = numerator < 0n ? -numerator : numerator;
    const d = denominator < 0n ? -denominator : denominator;
    const quotient = (n * 2n + d) / (2n * d);
    return negative ? -quotient : quotient;
}
const centimes = (value) => Number(value) / 100;
function refuse(message, code) {
    throw new InvoiceError(message, code, 400);
}
/** Refuses a rate outside 0 <= rate < 1 or with more than four decimals. */
export function assertGenericVatRate(rate) {
    const r = scaled(rate, 4);
    if (r === null || r < 0n || r >= 10000n) {
        refuse(`Taux de TVA invalide : ${String(rate)} (fraction de 0 à moins de 1, quatre décimales au plus)`, "TAUX_TVA_INVALIDE");
    }
    return r;
}
/** The stored amounts of one line. */
export function genericLineTax(line, priceMode) {
    const price = scaled(line.unitPrice, 2);
    if (price === null || price <= 0n) {
        refuse("Le prix unitaire doit être supérieur à zéro, au centime", "PRIX_INVALIDE");
    }
    const quantity = scaled(line.quantity, 3);
    if (quantity === null || quantity <= 0n) {
        refuse("La quantité doit être supérieure à zéro, trois décimales au plus", "QUANTITE_INVALIDE");
    }
    const discount = scaled(line.discount, 2);
    if (discount === null || discount < 0n) {
        refuse("La remise doit être positive ou nulle, au centime", "REMISE_INVALIDE");
    }
    const gross = price * quantity;
    if (discount * 1000n >= gross) {
        refuse("La remise doit être inférieure au montant de la ligne", "REMISE_INVALIDE");
    }
    const rate = assertGenericVatRate(line.rate.rate);
    const amount = divideHalfAway(gross - discount * 1000n, 1000n);
    let netHT;
    let vat;
    if (priceMode === "HT") {
        netHT = amount;
        vat = divideHalfAway(netHT * rate, 10000n);
    }
    else if (priceMode === "TTC") {
        netHT = divideHalfAway(amount * 10000n, 10000n + rate);
        vat = amount - netHT;
    }
    else {
        refuse(`Mode de prix invalide : ${String(priceMode)} (HT ou TTC)`, "INVALID_PRICE_MODE");
    }
    return {
        rateCode: line.rate.code,
        rate: Number(rate) / 10_000,
        netHT: centimes(netHT),
        vat: centimes(vat),
        ttc: centimes(netHT + vat),
    };
}
/** Sum of amounts to the centime, exact. */
function sumCentimes(values) {
    let total = 0n;
    for (const value of values)
        total += BigInt(Math.round(value * 100));
    return centimes(total);
}
/** The lines, their sums per rate and the invoice totals. */
export function genericInvoiceTaxes(lines, priceMode) {
    const computed = lines.map((line) => genericLineTax(line, priceMode));
    const byRate = {};
    for (const code of [...new Set(computed.map((line) => line.rateCode))]) {
        const atRate = computed.filter((line) => line.rateCode === code);
        const source = lines.find((line) => line.rate.code === code).rate;
        if (atRate.some((line) => line.rate !== atRate[0].rate)) {
            refuse(`Le taux ${code} porte deux valeurs sur la même facture`, "TAUX_TVA_INCOHERENT");
        }
        byRate[code] = {
            code,
            rate: atRate[0].rate,
            baseHT: sumCentimes(atRate.map((line) => line.netHT)),
            vat: sumCentimes(atRate.map((line) => line.vat)),
            ttc: sumCentimes(atRate.map((line) => line.ttc)),
            invoiceMention: source.invoiceMention ?? null,
        };
    }
    return {
        scheme: "GENERIC_VAT",
        lines: computed,
        byRate,
        totalHT: sumCentimes(computed.map((line) => line.netHT)),
        totalVAT: sumCentimes(computed.map((line) => line.vat)),
        totalTTC: sumCentimes(computed.map((line) => line.ttc)),
    };
}
/** The net to pay: the total TTC in the payable unit of `currency`. */
export function genericNetPayable(summary, currency) {
    return toPayableAmount(summary.totalTTC, currency);
}
//# sourceMappingURL=generic-vat.js.map